'use client';

import { getBrowserLocalStorage } from '@instamenu/browser-storage';
import { createContext, useContext, useCallback, useState, FC, PropsWithChildren } from 'react';
import { I18n, TranslateOptions } from 'i18n-js';
import { en } from '../localizations/en';
import { es } from '../localizations/es';
import { esAR } from '../localizations/es-AR';
import { enAU } from '../localizations/en-AU';
type Join<S1, S2> = S1 extends string ? (S2 extends string ? `${S1}.${S2}` : never) : never;

type Paths<T> = {
  [K in keyof T]: T[K] extends { zero: string; one: string; other: string }
    ? K // special case for https://www.npmjs.com/package/i18n-js#pluralization
    : T[K] extends Record<string, unknown>
      ? Join<K, Paths<T[K]>> // nested objects
      : K; // strings
}[keyof T];

export type CustomI18n = Omit<I18n, 't'> & {
  t: (scope: Paths<typeof en>, options?: TranslateOptions) => string;
};

interface LocaleContextProps {
  i18n: CustomI18n;
  setLocaleByValue: (locale: string) => void;
  storedLocale?: string;
}

const LocaleContext = createContext<LocaleContextProps | undefined>(undefined);

const defaultLocale = 'en';

function resolveLocale(selectedLocale: string, availableLocales: string[]): string {
  if (typeof navigator === 'undefined') return defaultLocale;

  const { language: languageTag } = navigator;
  const languageCode = languageTag.split('-')[0];

  const requestedLocale =
    selectedLocale === 'nav' || selectedLocale === languageCode ? languageTag : selectedLocale;

  if (availableLocales.includes(requestedLocale)) return requestedLocale;
  if (languageCode && availableLocales.includes(languageCode)) return languageCode;
  return defaultLocale;
}

const translations = {
  en,
  'en-AU': enAU,
  es,
  'es-AR': esAR,
};
const i18n: CustomI18n = new I18n(translations);
i18n.enableFallback = true;

export const LocaleProvider: FC<PropsWithChildren> = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    return getBrowserLocalStorage()?.getItem('selectedLocale') || 'nav';
  });

  const setLocaleByValue = useCallback((l: string) => {
    getBrowserLocalStorage()?.setItem('selectedLocale', l);
    setLocale(l);
  }, []);

  const availableLocales = Object.keys(translations);

  const resolvedLocale = resolveLocale(locale, availableLocales);
  i18n.locale = resolvedLocale;

  const contextValue: LocaleContextProps = {
    setLocaleByValue,
    i18n,
    storedLocale: locale,
  };

  return <LocaleContext.Provider value={contextValue}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
